import React, { useEffect, useMemo, useState } from 'react';
import {
  RawDataRowProvenance,
  RowLinkCandidate,
  RowLinkError,
  fetchRowLinkCandidates,
  linkRowToAnimal,
} from '../data/api';

// =============================================================================
// Link a scanned EID to an animal that was registered without one.
//
// Ingest assigns a scanned EID by itself whenever the receipt file shares an
// identifier with an existing animal. This panel covers the case it cannot:
// an abattoir row carrying an EID and an ear-tag number against an animal
// registered at the farm with only a VID. Nothing matches automatically, so a
// person decides — and the screen's job is to make that decision fast and hard
// to get wrong.
//
// Deliberately no auto-selection. The panel narrows the list to animals that
// can legally take the tag and highlights which fields agree; the choice stays
// with the operator.
//
// Shared by all three entry points (audit-trail drill-in, the deal reconcile
// queue, and the post-upload summary) so the interaction is identical wherever
// an unlinked row is found.
// =============================================================================

const FIELD_LABELS: Record<string, string> = {
  vid: 'VID',
  sex: 'Sex',
  dob: 'Date of birth',
  other_id_1: 'Other ID',
};

const SEX_LABELS: Record<string, string> = { M: 'Male', F: 'Female' };

const fmt = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

/** The handful of fields an operator actually compares, pulled off the raw row. */
function rowSummary(row: RawDataRowProvenance): Array<[string, string]> {
  const mapped = row.mapped_data || {};
  const raw = row.raw_data || {};
  const pick = (...keys: string[]) => {
    for (const key of keys) {
      const value = (mapped as any)[key] ?? (raw as any)[key];
      if (value !== null && value !== undefined && value !== '') return String(value);
    }
    return '';
  };
  return [
    ['VID', pick('vid', 'VID')],
    ['Ear tag', pick('ear_tag', 'Ear Tag')],
    ['Sex', pick('sex', 'Sex')],
    ['Date of birth', pick('dob', 'date_of_birth', 'Birth Date')],
    ['Species', pick('species', 'Species')],
    ['Row', String(row.row_number ?? '')],
  ].filter(([, value]) => value !== '') as Array<[string, string]>;
}

function AgreementChips({ fields }: { fields: string[] }) {
  if (!fields || fields.length === 0) {
    return <span className="text-[11px] text-avcs-muted">No fields in common</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {fields.map((field) => (
        <span
          key={field}
          className="inline-flex items-center gap-1 rounded-full border border-[#b7dcc0] bg-[#eaf7ee] px-1.5 py-0.5 text-[11px] font-semibold text-[#1c6b32]"
        >
          {/* Not colour-only: the tick carries the meaning for anyone who can't
              distinguish the green. */}
          <span aria-hidden="true">✓</span>
          {FIELD_LABELS[field] || field} matches
        </span>
      ))}
    </span>
  );
}

export interface LinkEidPanelProps {
  row: RawDataRowProvenance;
  /** Called after a successful link so the caller can refresh its own data. */
  onLinked?: (result: any) => void;
  onCancel?: () => void;
}

export function LinkEidPanel({ row, onLinked, onCancel }: LinkEidPanelProps) {
  const [candidates, setCandidates] = useState<RowLinkCandidate[]>([]);
  const [scannedEid, setScannedEid] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<RowLinkError | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [showAllFields, setShowAllFields] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchRowLinkCandidates(row.id)
      .then((data) => {
        if (cancelled) return;
        setCandidates(data.results);
        setScannedEid(data.scanned_eid);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Could not load candidate animals.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [row.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      [c.vid, c.other_id_1, c.species, c.sex, c.dob, c.current_entity]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q));
  }, [candidates, search]);

  const selected = candidates.find((c) => c.id === selectedId) || null;
  const summary = rowSummary(row);
  const allFields = Object.entries(row.raw_data || {});

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    setConflict(null);
    try {
      const result = await linkRowToAnimal(row.id, selected.id);
      onLinked?.(result);
    } catch (e: any) {
      // A 409 is a different situation with a different remedy, not a failure
      // to be retried — surface it distinctly (see the conflict banner below).
      if (e instanceof RowLinkError && e.conflict) setConflict(e);
      setError(e?.message || 'Could not link this row.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* ---- The scanned row -------------------------------------------- */}
      <div className="rounded-xl border border-[#1e6bb824] bg-[#f8fbff] p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-avcs-muted">Scanned EID</div>
            <div className="font-mono text-lg font-semibold text-avcs-text">{scannedEid || '—'}</div>
          </div>
          <div className="text-xs text-avcs-muted">
            {row.original_filename ? <span className="font-mono">{row.original_filename}</span> : null}
            {row.row_number != null ? ` · row ${row.row_number}` : ''}
          </div>
        </div>

        {summary.length > 0 && (
          <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
            {summary.map(([label, value]) => (
              <div key={label} className="min-w-0 text-xs">
                <dt className="truncate text-avcs-muted">{label}</dt>
                <dd className="truncate font-medium text-avcs-text" title={value}>{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* Progressive disclosure: the summary above is what the decision is
            actually made on; the full row is there when it isn't enough. */}
        {allFields.length > 0 && (
          <>
            <button
              type="button"
              className="mt-2 text-xs font-semibold text-[#1e6bb8] hover:underline"
              aria-expanded={showAllFields}
              onClick={() => setShowAllFields((v) => !v)}
            >
              {showAllFields ? 'Hide' : `Show all ${allFields.length} raw fields`}
            </button>
            {showAllFields && (
              <div className="mt-2 grid max-h-48 gap-x-4 gap-y-1 overflow-auto rounded-lg border border-[#1e6bb814] bg-white p-2 sm:grid-cols-2 lg:grid-cols-3">
                {allFields.map(([key, value]) => (
                  <div key={key} className="min-w-0 text-xs">
                    <span className="block truncate text-avcs-muted" title={key}>{key}</span>
                    <span className="block truncate text-avcs-text" title={fmt(value)}>{fmt(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ---- Candidates -------------------------------------------------- */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h4 className="text-sm font-semibold text-avcs-text">
            Un-tagged animals on this deal
            {!loading && <span className="ml-1 font-normal text-avcs-muted">({candidates.length})</span>}
          </h4>
          {candidates.length > 0 && (
            <input
              className="rounded-lg border border-[#1e6bb833] px-2 py-1 text-xs md:w-64"
              placeholder="Search VID, other ID, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search candidate animals"
            />
          )}
        </div>

        {loading ? (
          <p className="text-sm text-avcs-muted">Loading candidates…</p>
        ) : candidates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#1e6bb833] bg-[#fbfeff] p-4 text-sm text-avcs-muted">
            <p className="font-medium text-avcs-text">No un-tagged animals on this deal.</p>
            <p className="mt-1">
              Every animal already carries an EID, so this row has nothing to link to. If the
              animal exists under a different tag, the two records need merging rather than linking.
            </p>
          </div>
        ) : (
          <div className="max-h-72 overflow-auto rounded-lg border border-[#1e6bb824]">
            <ul className="divide-y divide-[#1e6bb812]">
              {filtered.map((candidate) => {
                const isSelected = candidate.id === selectedId;
                return (
                  <li key={candidate.id}>
                    <label
                      className={`flex cursor-pointer items-start gap-3 px-3 py-2 text-xs hover:bg-[#f8fbff] ${
                        isSelected ? 'bg-[#eef5ff]' : 'bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`link-candidate-${row.id}`}
                        className="mt-0.5"
                        checked={isSelected}
                        onChange={() => setSelectedId(candidate.id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                          <span className="font-mono text-sm font-semibold text-avcs-text">
                            {candidate.vid || candidate.other_id_1 || 'No VID'}
                          </span>
                          <span className="text-avcs-muted">{fmt(candidate.species)}</span>
                          <span className="text-avcs-muted">{SEX_LABELS[candidate.sex || ''] || fmt(candidate.sex)}</span>
                          <span className="text-avcs-muted">DOB {fmt(candidate.dob)}</span>
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-2">
                          <AgreementChips fields={candidate.agreement} />
                          {candidate.current_entity && (
                            <span className="text-[11px] text-avcs-muted">at {candidate.current_entity}</span>
                          )}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-xs text-avcs-muted">No candidate matches “{search}”.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* ---- Conflict / error -------------------------------------------- */}
      {error && (
        // role="alert" so a screen reader hears it; the conflict variant gets
        // its own wording because the fix is different (merge, not retry).
        <div role="alert" className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">
          {error}
          {conflict?.conflict === 'eid_held_by_another_animal' && (
            <p className="mt-1 text-xs">
              Two records exist for one animal. Linking cannot move a tag between them
              {conflict.holderDeleted ? ' — and the record holding this tag is currently deleted' : ''}.
            </p>
          )}
        </div>
      )}

      {/* ---- Confirm ------------------------------------------------------ */}
      <div className="flex flex-col gap-2 border-t border-[#1e6bb814] pt-3 md:flex-row md:items-center md:justify-between">
        <p className="text-xs text-avcs-muted">
          {selected ? (
            <>
              Assign <span className="font-mono font-semibold text-avcs-text">{scannedEid}</span> to{' '}
              <span className="font-semibold text-avcs-text">{selected.vid || selected.other_id_1 || 'this animal'}</span>.
              The event's animal count updates to match.
            </>
          ) : (
            'Select the animal this row belongs to.'
          )}
        </p>
        <div className="flex gap-2">
          {onCancel && (
            <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          )}
          <button
            type="button"
            className="btn primary"
            onClick={submit}
            disabled={!selected || saving}
          >
            {saving ? 'Linking…' : 'Link EID to animal'}
          </button>
        </div>
      </div>
    </div>
  );
}
